import { useEffect, useRef, useState } from "react";
import {
  Upload,
  FileText,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  X,
} from "lucide-react";
import { Modal } from "../components/Modal";
import {
  ValidationResultPanel,
  isValidationPassed,
} from "../components/ValidationResultPanel";
import { ValidationProgress } from "../components/ValidationProgress";
import { showToast } from "../components/ui/custom-toast";
import {
  validateGovernanceDocumentFile,
  uploadGovernanceDocumentFile,
  ApiEnvelopeError,
  type ValidateFileResult,
} from "../../api/frameworksApi";

// ─── Constraints ─────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = "25 MB";

// ─── Error code → friendly message ──────────────────────────────────────────

const ERROR_CODE_MESSAGES: Record<string, (detail: string) => string> = {
  "REG-2001-E": (d) =>
    `We couldn't read this PDF: ${d} Please pick a different file.`,
  "CR-0301-E": () =>
    "This document doesn't have an organization assigned yet. Set the organization on the document first, then try again.",
  "LLM-0005-E": () =>
    "No active LLM is configured. Ask your administrator to activate one in Settings → AI → LLM Configurations.",
  "LLM-0001-E": () =>
    "The active LLM rejected the request — its credentials may be invalid. Ask your administrator to check.",
  "LLM-0003-E": () =>
    "The active LLM model is not available. Ask your administrator to check the configuration.",
  "LLM-0004-E": () => "Couldn't reach the LLM provider. Try again in a moment.",
  "LLM-0099-E": () =>
    "Validation result couldn't be parsed. Try again, or pick a different model.",
  "SYS-0002-E": () => "Invalid file. Please pick a PDF.",
  "CR-0102-E": () => "This document is no longer editable.",
  "CR-0212-E": (d) => `File upload failed: ${d}`,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ModalState = "PICKING" | "VALIDATING" | "RESULTS" | "UPLOADING" | "ERROR";

interface DisplayError {
  title: string;
  message: string;
  providerMessage?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  orgName?: string | null;
  onUploaded: () => void;
  onDiscarded?: () => void;
  brand?: string;
  docVersion?: string;
  docType?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function mapError(e: unknown): DisplayError {
  if (e instanceof ApiEnvelopeError) {
    const mapper = ERROR_CODE_MESSAGES[e.code];
    return {
      title: e.title || "Error",
      message: mapper ? mapper(e.detail) : e.detail,
      providerMessage: e.providerMessage,
    };
  }
  if (e instanceof Error) {
    return { title: e.name || "Error", message: e.message };
  }
  return { title: "Error", message: "Something went wrong." };
}

function isAbortError(e: unknown): boolean {
  return (
    (e instanceof DOMException && e.name === "AbortError") ||
    (e instanceof Error && e.name === "AbortError")
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function GovernanceUploadModal({
  open,
  onClose,
  documentId,
  documentTitle,
  orgName,
  onUploaded,
  onDiscarded,
}: Props) {
  const [state, setState] = useState<ModalState>("PICKING");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ValidateFileResult | null>(null);
  const [error, setError] = useState<DisplayError | null>(null);
  const [lastAction, setLastAction] = useState<"validate" | "upload" | null>(
    null,
  );
  const [pickError, setPickError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) {
      setState("PICKING");
      setFile(null);
      setResult(null);
      setError(null);
      setLastAction(null);
      setPickError(null);
      setDragActive(false);
    }
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [open]);

  // ── Validate ─────────────────────────────────────────────────────────────
  const runValidate = async (f: File) => {
    setLastAction("validate");
    setState("VALIDATING");
    setError(null);
    setResult(null);

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const data = await validateGovernanceDocumentFile(
        documentId,
        f,
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      setResult(data);
      setState("RESULTS");
    } catch (e: unknown) {
      if (isAbortError(e)) return;
      setError(mapError(e));
      setState("ERROR");
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  };

  // ── Upload ───────────────────────────────────────────────────────────────
  const runUpload = async () => {
    if (!file) return;
    setLastAction("upload");
    setState("UPLOADING");
    setError(null);
    try {
      await uploadGovernanceDocumentFile(documentId, file);
      showToast("success", `Uploaded "${file.name}".`);
      onUploaded();
      onClose();
    } catch (e: unknown) {
      if (e instanceof ApiEnvelopeError && e.code === "CR-0303-E") {
        const discarded = e.detail.includes("discarded");
        if (discarded) {
          showToast(
            "error",
            "Draft discarded. The file did not match this document's metadata or org rules. Re-create the document and upload a matching file.",
          );
          (onDiscarded ?? onClose)();
        } else {
          showToast(
            "error",
            "Upload rejected. The new file did not pass validation; the previously-uploaded file is retained.",
          );
          onUploaded();
          onClose();
        }
        return;
      }
      setError(mapError(e));
      setState("ERROR");
    }
  };

  // ── File picker handler ──────────────────────────────────────────────────
  const pickFile = (f: File | null) => {
    setPickError(null);
    if (!f) return;
    const name = f.name.toLowerCase();
    const isPdf = f.type === "application/pdf" || name.endsWith(".pdf");
    const isDoc =
      f.type === "application/msword" ||
      f.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      name.endsWith(".doc") ||
      name.endsWith(".docx");
    if (!isPdf && !isDoc) {
      setPickError("Only PDF and Word documents (.doc, .docx) are supported.");
      return;
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      setPickError(`File is larger than ${MAX_FILE_SIZE_LABEL}.`);
      return;
    }
    setFile(f);
    runValidate(f);
  };

  const goPicking = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setFile(null);
    setResult(null);
    setError(null);
    setLastAction(null);
    setPickError(null);
    setState("PICKING");
  };

  const cancelValidating = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    goPicking();
  };

  const handleRetry = () => {
    if (lastAction === "upload") runUpload();
    else if (file) runValidate(file);
    else goPicking();
  };

  // ─── Title per state ────────────────────────────────────────────────────
  const titleByState: Record<ModalState, string> = {
    PICKING: `Upload to: ${documentTitle}`,
    VALIDATING: "Validating document…",
    RESULTS: "Validation Results",
    UPLOADING: "Uploading…",
    ERROR: "Something went wrong",
  };

  // ─── Footer per state ───────────────────────────────────────────────────
  const renderFooter = () => {
    switch (state) {
      case "PICKING":
        return (
          <div className="flex w-full justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
          </div>
        );
      case "VALIDATING":
        return (
          <div className="flex w-full justify-end">
            <button
              onClick={cancelValidating}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors flex items-center gap-2"
            >
              <X className="size-3.5" /> Cancel
            </button>
          </div>
        );
      case "UPLOADING":
        return (
          <div className="flex w-full justify-end">
            <button
              disabled
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 opacity-40 cursor-not-allowed"
            >
              Uploading…
            </button>
          </div>
        );
      case "RESULTS": {
        const canUpload = result ? isValidationPassed(result) : false;
        return (
          <div className="flex w-full items-center gap-2">
            {/* Back — left side */}
            <button
              onClick={goPicking}
              className="p-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
              title="Choose a different file"
            >
              <ArrowLeft className="size-4" />
            </button>

            <div className="flex-1" />

            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={runUpload}
              disabled={!canUpload}
              title={
                canUpload
                  ? undefined
                  : "Validation failed — pick a different file to enable upload."
              }
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
            >
              <Upload className="size-3.5" /> Upload
            </button>
          </div>
        );
      }
      case "ERROR":
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={goPicking}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Choose Different File
            </button>
            <button
              onClick={handleRetry}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20"
            >
              Retry
            </button>
          </div>
        );
    }
  };

  // ─── Body per state ─────────────────────────────────────────────────────
  const renderBody = () => {
    switch (state) {
      case "PICKING":
        return (
          <div className="space-y-3">
            <label
              onDragEnter={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                pickFile(e.dataTransfer.files?.[0] ?? null);
              }}
              className={`flex flex-col items-center gap-3 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                dragActive
                  ? "border-indigo-500 bg-indigo-50/40 dark:bg-indigo-500/5"
                  : "border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-400"
              }`}
            >
              <Upload className="size-8 d" />
              <div className="text-center space-y-0.5">
                <p className="text-sm font-medium text-foreground">
                  Drop a PDF or Word document here, or click to browse
                </p>
                <p className="text-xs d">
                  We'll check it against{" "}
                  <span className="font-medium">
                    {orgName ? `${orgName}'s` : "your organization's"}
                  </span>{" "}
                  active rules before upload.
                </p>
                <p className="text-xs d">
                  PDF, DOC, DOCX · up to {MAX_FILE_SIZE_LABEL}
                </p>
              </div>
              <input
                type="file"
                accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {pickError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p>{pickError}</p>
              </div>
            )}
          </div>
        );

      case "VALIDATING":
        return (
          <ValidationProgress
            fileName={file?.name}
            fileSize={file ? formatFileSize(file.size) : undefined}
          />
        );

      case "UPLOADING":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="size-8 animate-spin text-indigo-500" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">Uploading…</p>
              {file && (
                <p className="text-xs d">
                  <FileText className="inline size-3 mr-1 -mt-0.5" />
                  {file.name}
                </p>
              )}
            </div>
          </div>
        );

      case "RESULTS":
        if (!result) return null;
        return <ValidationResultPanel result={result} />;

      case "ERROR":
        return (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
              <AlertTriangle className="size-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
              <div className="space-y-0.5 min-w-0">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                  {error?.title ?? "Error"}
                </p>
                <p className="text-sm text-red-700/90 dark:text-red-400/90">
                  {error?.message ?? "Something went wrong."}
                </p>
              </div>
            </div>
            {error?.providerMessage && (
              <details className="text-xs text-[#64748b] dark:text-white/40">
                <summary className="cursor-pointer hover:text-[#475569] dark:hover:text-white/60 select-none font-medium">
                  Show LLM details
                </summary>
                <pre className="mt-1.5 p-2.5 rounded-md bg-[#f8fafc] dark:bg-white/[0.03] whitespace-pre-wrap break-words border border-[#e2e8f0] dark:border-white/[0.06] leading-relaxed font-mono">
                  {error.providerMessage}
                </pre>
              </details>
            )}
          </div>
        );
    }
  };

  const notClosable = state === "VALIDATING" || state === "UPLOADING";

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!notClosable) onClose();
      }}
      title={titleByState[state]}
      description={
        state === "PICKING"
          ? `Validation runs against ${orgName ?? "this org"}'s active rules before the file is saved.`
          : undefined
      }
      size="lg"
      closable={!notClosable}
      footer={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );
}
